import { graphql } from 'gatsby';
import React, { useRef } from 'react';

import { PrimaryLink } from '../components/Common/Links';
import RichTextConverter from '../components/Common/RichText/RichTextConverter';
import ScrollTo, { smoothScrollToRef } from '../components/Common/ScrollTo';
import Layout, { BACKGROUND } from '../components/Layouts/Layout';
import LearningCenterSeo from '../components/LearningCenter/LearningCenterSeo';
import HeadingWithForm from '../components/LearningCenter/PostHeaders/HeadingWithForm';
import PageSection from '../components/PageSections/PageSection';
import { ReactComponent as Placeholder } from '../images/Blank headshot.svg';
import * as styles from './event.module.scss';

const Event = ({ data: { content }, pageContext }) => {
  const formData = {
    title: content.title,
    successCopy: content?.submissionSuccessMessage || content.form?.formSuccessMessage,
    fileUrl: content?.downloadableContent?.file?.url || '',
    contentIsDownloadable: content.contentIsDownloadable,
    heroImage: content.heroImage,
    marketoFormId: content.marketoFormId,
    dynamicForm: content.form,
  };

  const scrollRef = useRef();
  const onScrollCta = (e) => {
    e.preventDefault();
    smoothScrollToRef(scrollRef, null, 84);
  };

  const logos = content.logos || [];
  const superText = content?.superHeading || content?.description?.description;
  const showFooterCta = !content.hideFooterCta && !content.availableForAll;
  const displayBottomSection = !!content.speakers || !!content.logos || showFooterCta;
  return (
    <Layout background={BACKGROUND.GREY}>
      <ScrollTo reference={scrollRef} />
      <div className={'publish-date'} data-post-date={content.publishDate} />
      <HeadingWithForm
        title={content.title}
        headingFontColor={content.headingFontColor}
        backgroundImageUrl={content?.headingBackground?.file?.url}
        formData={formData}
        superText={superText}
        excerpt={content?.bodyContent}
        availableForAll={content.availableForAll}
        headingImage={content.headingImage}
      />

      {content.availableForAll && content.webinarContent && (
        <PageSection data-analytics-section={'webinar-content'}>
          <RichTextConverter copy={content.webinarContent} />
        </PageSection>
      )}

      {displayBottomSection && (
        <PageSection data-analytics-section={'event-info'}>
          {content.speakers && (
            <>
              <h3 className={styles.webinarHeading}>{content.speakersHeading || 'Speakers'}</h3>
              <div className={styles.speakers}>
                {content.speakers.map((speaker, i) => {
                  const hasImage = !!speaker?.image?.file?.url;
                  let image = <img alt={'speaker'} src={speaker?.image?.file?.url} />;
                  if (!hasImage) {
                    image = <Placeholder />;
                  }
                  return (
                    <div className={styles.speakerContainer} key={i}>
                      <div className={styles.speakerImg}>{image}</div>
                      <div className={styles.speakerText}>
                        <span className={styles.speakerName}>{speaker.name}</span>
                        <span className={styles.speakerTitle}>{`${speaker.title} at ${speaker.company}`}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {showFooterCta && (
            <div className={styles.registerContainer}>
              <PrimaryLink
                className={styles.register}
                label={content.footerCtaLabel || 'Register Now'}
                onClick={onScrollCta}
                isButton
              />
            </div>
          )}

          {logos && (
            <div className={styles.logos} data-analytics-section={'logos'}>
              {logos.map((logo, i) => {
                return (
                  <img
                    data-analytics-component={'logo'}
                    data-analytics-id={logo.title}
                    alt={'Logo'}
                    className={styles.logo}
                    src={logo.file.url}
                    key={i}
                  />
                );
              })}
            </div>
          )}
        </PageSection>
      )}
    </Layout>
  );
};

export const Head = ({ data, location }) => <LearningCenterSeo meta={data.content.pageMeta} slug={location.pathname} />;

export default Event;

export const pageQuery = graphql`
  query EventQuery($pageId: String!) {
    content: contentfulPageEvent(contentful_id: { eq: $pageId }) {
      ...Event
    }
  }
`;
